"use client";

import { JSX, MouseEvent } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useViewerLanguage } from "@/lib/i18n";
import { emitModuleInteraction } from "@/lib/module-interaction";

export function pageCountOf(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
}

export function ModulePager({
  page,
  pageSize,
  total,
  onPageChange,
  className,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const pages = pageCountOf(total, pageSize);
  const current = Math.min(Math.max(0, page), pages - 1);
  const first = current * pageSize + 1;
  const last = Math.min(total, (current + 1) * pageSize);

  const go = (to: number, e: MouseEvent<HTMLButtonElement>) => {
    if (to < 0 || to >= pages || to === current) return;
    emitModuleInteraction(e.currentTarget, "page_change", { kind: "control", id: "pager", index: to }, {
      from: current,
      to,
      pages,
    });
    onPageChange(to);
  };

  return (
    <div
      className={cn(
        "flex h-8 items-center justify-between gap-3 text-xs text-[var(--subtle)]",
        className,
      )}
    >
      <span className="truncate tabular-nums">
        {total > 0
          ? t("{first}–{last} of {total}", { first, last, total })
          : t("0 entries")}
      </span>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t("Previous page")}
          disabled={current === 0}
          onClick={(e) => go(current - 1, e)}
          className="border border-[var(--border)] text-[var(--muted)] hover:bg-[var(--track)] hover:text-[var(--fg)] dark:hover:bg-[var(--track)]"
        >
          <ChevronLeft className="size-3.5" />
        </Button>
        <span className="px-1.5 tabular-nums" aria-live="polite">
          {t("Page {page} of {pages}", { page: current + 1, pages })}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t("Next page")}
          disabled={current >= pages - 1}
          onClick={(e) => go(current + 1, e)}
          className="border border-[var(--border)] text-[var(--muted)] hover:bg-[var(--track)] hover:text-[var(--fg)] dark:hover:bg-[var(--track)]"
        >
          <ChevronRight className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}
